import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, Twitter, Linkedin, MessageCircle } from 'lucide-react';

const Footer = () => {

  return (
    <footer id="contact" className="relative bg-[#bceb39] overflow-hidden">
      {/* Main Footer Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Left Section - Logo, App Buttons & Privacy Policy - Center aligned on mobile */}
          <div className="space-y-6 text-center md:text-left flex flex-col items-center md:items-start">
            <Link to="/">
              <img
                src="https://customer-assets.emergentagent.com/job_electric-rides-6/artifacts/279asuc7_white%20black%20logo.png"
                alt="NURA"
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-gray-900 text-lg font-medium">
              Experience the future of transportation with our all-electric fleet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <a
                href="https://app.nuraemobility.co.in/?c=YImSUF"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_electric-rides-6/artifacts/hi2yb4wg_App%20Store.png"
                  alt="Download on the App Store"
                  className="h-12 w-auto"
                />
              </a>
              <a
                href="https://app.nuraemobility.co.in/?c=YImSUF"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_electric-rides-6/artifacts/bqk1ja7h_Play%20Store.png"
                  alt="Get it on Google Play"
                  className="h-12 w-auto"
                />
              </a>
            </div>

            {/* Privacy Policy & Terms */}
            <div className="pt-4">
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <a href="https://nuraemobility.co.in/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-gray-900 hover:text-gray-700 font-medium transition-colors">
                  Privacy Policy
                </a>
                <span className="text-gray-900">|</span>
                <a href="https://nuraemobility.co.in/terms" target="_blank" rel="noopener noreferrer" className="text-gray-900 hover:text-gray-700 font-medium transition-colors">
                  Terms & Conditions
                </a>
              </div>
            </div>
          </div>

          {/* Middle Section - Quick Links */}
          <div className="space-y-6 flex flex-col items-center md:items-start">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-900 hover:text-gray-700 font-medium transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-900 hover:text-gray-700 font-medium transition-colors">
                  Our Services
                </Link>
              </li>
              <li>
                <Link to="/drive-with-us" className="text-gray-900 hover:text-gray-700 font-medium transition-colors">
                  Drive with us
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-900 hover:text-gray-700 font-medium transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/safety" className="text-gray-900 hover:text-gray-700 font-medium transition-colors">
                  Safety
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-900 hover:text-gray-700 font-medium transition-colors">
                  Contact us
                </Link>
              </li>
            </ul>
          </div>

          {/* Right Section - Contact Info & Social - Container centered, content left-aligned */}
          <div className="space-y-6 flex flex-col items-center md:items-start w-full">
            <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center md:text-left w-full md:w-auto">Contact Us</h3>

            {/* Centered container with left-aligned content */}
            <div className="flex flex-col items-center md:items-start w-full gap-5">

              {/* Office Address */}
              <div>
                <p className="text-gray-900 font-bold text-base mb-1">Office Address</p>
                <div className="flex items-start gap-3">
                  <div className="w-5 flex justify-center flex-shrink-0 mt-0.5">
                    <MapPin size={18} className="text-gray-900" />
                  </div>
                  <span className="text-gray-900 font-medium text-left">
                    42, Gandhi Mandapam Rd<br />
                    Chitra Nagar, Kotturpuram<br />
                    Chennai – 600085
                  </span>
                </div>
              </div>

              {/* Talk to Us */}
              <div>
                <p className="text-gray-900 font-bold text-base mb-2">Talk to Us</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-5 flex justify-center flex-shrink-0 mt-0.5">
                      <Phone size={18} className="text-gray-900" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">Customer Support</p>
                      <a href="tel:+919566053118" className="text-gray-900 font-medium hover:text-gray-700 transition-colors duration-300">
                        +91 9566053118
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-5 flex justify-center flex-shrink-0 mt-0.5">
                      <Phone size={18} className="text-gray-900" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">Careers</p>
                      <a href="tel:+919566003924" className="text-gray-900 font-medium hover:text-gray-700 transition-colors duration-300">
                        +91 9566003924
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-5 flex justify-center flex-shrink-0 mt-0.5">
                      <Phone size={18} className="text-gray-900" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">Driver Onboarding</p>
                      <a href="tel:+919566004019" className="text-gray-900 font-medium hover:text-gray-700 transition-colors duration-300">
                        +91 9566004019
                      </a>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Email */}
              <div className="flex items-start gap-3">
                <div className="w-5 flex justify-center flex-shrink-0 mt-0.5">
                  <Mail size={18} className="text-gray-900" />
                </div>
                <div>
                  <p className="text-gray-900 font-semibold text-sm">Email</p>
                  <a href="mailto:info@nuraemobility.co.in" className="text-gray-900 font-medium hover:text-gray-700 transition-colors duration-300">
                    info@nuraemobility.co.in
                  </a>
                </div>
              </div>

              {/* Follow Us */}
              <div className="w-full">
                <p className="text-gray-900 font-bold text-base mb-3 text-center md:text-left">Follow us:</p>
                <div className="flex gap-4 justify-center md:justify-start">
                  <a href="https://www.facebook.com/people/NURA-Electric-Mobility/61579589855918/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center text-white hover:bg-gray-800 transition-colors">
                    <Facebook size={22} />
                  </a>
                  <a href="https://www.instagram.com/nura_electric_mobility" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center text-white hover:bg-gray-800 transition-colors">
                    <Instagram size={22} />
                  </a>
                  <a href="https://www.linkedin.com/company/nura-electric-mobility/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center text-white hover:bg-gray-800 transition-colors">
                    <Linkedin size={22} />
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Extra padding for skyline visibility */}
        <div style={{ paddingBottom: '300px' }}></div>
      </div>

      {/* Chennai Skyline Background with Animated Auto - Covers full height */}
      <div className="absolute bottom-0 left-0 right-0 z-0" style={{ height: '400px' }}>
        <img
          src="https://customer-assets.emergentagent.com/job_electric-rides-6/artifacts/56cbtj11_chennai%20skyline.png"
          alt="Chennai Skyline"
          className="w-full h-full object-cover object-bottom"
        />

        {/* Single Animated Auto - right to left, wheels on road, 1.5x speed */}
        <div className="absolute bottom-8 left-0 right-0 animate-drive-fixed">
          <img
            src="https://customer-assets.emergentagent.com/job_electric-rides-6/artifacts/014b797m_women-driver-BNJh4seE.png"
            alt="NURA Auto"
            className="h-20 w-auto"
            style={{ transform: 'scale(3)', transformOrigin: 'center' }}
          />
        </div>
      </div>
    </footer>
  );
};

export default Footer;